import { type VolunteeringRecordDto } from "@crowdcoursing/api/openapi";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { type GridFilterOperator, type GridRowParams, useGridApiRef } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";

import { useSelector } from "~hooks/index";
import { Route } from "~services/router/Route";
import { type DateRangeValue, type FIELD_NAME_DATE_RANGE } from "~ui/atoms/Form";
import { Link } from "~ui/atoms/Link/Link";
import { DataGrid, type DataGridProps } from "~ui/molecules/DataGrid/DataGrid";
import { VolunteeringRecordPanel } from "~ui/molecules/UserHistory/VolunteeringRecordPanel";
import { formatEventTime, formatEventTimes } from "~utils/date";

import { VolunteersHeaderWidgets } from "./VolunteersHeaderWidgets";

const tEvent = "Event";
const tEventTime = "Event Time";
const tTotalMinutes = "Time";
const tHours = "hrs";
const tDetails = "Details";
const tClickForDetails = "Click for more details";

export type DataGridVolunteer = Pick<
  VolunteeringRecordDto,
  | "eventName"
  | "eventId"
  | "startTime"
  | "endTime"
  | "leaderDisplayName"
  | "totalMinutes"
  | "totalMinutesConfirmed"
  | "volunteerUserId"
  | "id"
>;

export type RowData = Partial<{
  confirmed: boolean;
  eventTime: {
    startTime: DateTime | null;
    endTime: DateTime | null;
  };
  leader: {
    displayName: string;
  };
  event: {
    id: string;
    name: string;
  };
  totalMinutes: number;
  recordId: string;
}> & {
  rowId: string;
  userId: string;
};

const eventTimeGetter = (row: RowData) => {
  const { eventTime } = row;
  if (!eventTime) {
    return "";
  }

  const { startTime, endTime } = eventTime;
  if (!startTime || !endTime) {
    return "";
  }

  if (startTime && !endTime) {
    return formatEventTime(startTime.toJSDate());
  }

  return formatEventTimes(startTime.toJSDate(), endTime.toJSDate(), { omitDateWhenCurrentYear: true });
};

const columns: DataGridProps<RowData>["columns"] = [
  {
    field: "rowId",
    filterable: false,
    flex: 0,
    headerName: tDetails,
    maxWidth: 50,
    renderCell: () => (
      <Tooltip title={tClickForDetails}>
        <IconButton
          size="small"
          sx={{
            "&:hover": {
              color: "primary.main",
            },
            color: "action.active",
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
    ),
    sortable: false,
    width: 50,
  },
  {
    field: "totalMinutes",
    flex: 0,
    headerName: tTotalMinutes,
    maxWidth: 100,
    minWidth: 60,
    renderCell: (params) => {
      const minutes = params.row.totalMinutes || 0;
      const hours = (minutes / 60).toFixed(2);
      return `${hours} ${tHours}`;
    },
    valueGetter: (params) => {
      const minutes = params.row.totalMinutes || 0;
      const hours = (minutes / 60).toFixed(2);
      return `${hours} ${tHours}`;
    },
  },
  {
    field: "event",
    filterable: true,
    flex: 1,
    headerName: tEvent,
    minWidth: 120,
    renderCell: (params) => {
      const StatusIcon = params.row.confirmed ? CheckCircleIcon : ScheduleIcon;
      const statusTooltip = params.row.confirmed ? "Confirmed" : "Pending";
      return (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1, minWidth: 0 }}>
          <Tooltip title={statusTooltip}>
            <StatusIcon
              fontSize="small"
              sx={{
                color: params.row.confirmed ? "success.main" : "action.disabled",
                flexShrink: 0,
              }}
            />
          </Tooltip>
          <Link type="internal" to={Route.eventView({ eventId: params.row.event?.id ?? "" })} sx={{ fontWeight: 600 }}>
            {params.row.event?.name ?? ""}
          </Link>
        </Box>
      );
    },
    valueGetter: (params) => {
      return params.row.event?.name ?? "";
    },
  },
  {
    field: "eventTime",
    filterOperators: [
      {
        getApplyFilterFn: () => null,
        getApplyFilterFnV7: (filterItem) => {
          if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
            return null;
          }

          if (filterItem.value.some((value) => !value)) {
            return null;
          }

          // (value, row, column, apiRef) => null | boolean
          return (_, row) => {
            const { startTime, endTime } = row.eventTime;
            const [filterBegin, filterEnd]: DateRangeValue = filterItem.value;

            if (!filterBegin || !filterEnd) {
              return false;
            }

            return startTime >= filterBegin.startOf("day").toJSDate() && endTime <= filterEnd.endOf("day").toJSDate();
          };
        },
        label: "Between",
        value: "between",
      } satisfies GridFilterOperator,
    ],
    filterable: true,
    flex: 0,
    headerName: tEventTime,
    maxWidth: 150,
    minWidth: 90,
    renderCell: ({ row }) => eventTimeGetter(row),
    sortComparator: (a, b) => {
      // a and b are timestamps from valueGetter
      // Handle null/undefined values - put them at the end
      if (a === 0 && b === 0) {
        return 0;
      }
      if (a === 0) {
        return 1;
      }
      if (b === 0) {
        return -1;
      }

      return a - b; // Numeric comparison of timestamps
    },
    valueGetter: ({ row }) => row.eventTime?.startTime?.toMillis() || 0,
    width: 130,
  },
];

const generateRow = (data: DataGridVolunteer, userId: string): RowData => {
  return {
    confirmed: !!data.totalMinutesConfirmed,
    event: {
      id: data.eventId ?? "MISSING_EVENT_ID",
      name: data.eventName ?? "MISSING_EVENT_NAME",
    },
    eventTime: {
      endTime: data.endTime ? DateTime.fromISO(data.endTime) : null,
      startTime: data.startTime ? DateTime.fromISO(data.startTime) : null,
    },
    leader: {
      displayName: data.leaderDisplayName?.trim() || "Unknown",
    },
    recordId: data.id ?? crypto.randomUUID(),
    rowId: crypto.randomUUID(),
    totalMinutes: data.totalMinutes,
    userId,
  };
};

export interface VolunteersProps {
  data?: DataGridVolunteer[];
  status?: Utils.QueryStatus;
  userId?: string;
  refetch?: () => void;
}

export interface FilterFormValues {
  [FIELD_NAME_DATE_RANGE]: DateRangeValue;
}

export const Volunteers = ({ userId, refetch, ...props }: VolunteersProps) => {
  const { data, status } = props;
  const apiRef = useGridApiRef();
  const [selectedRecord, setSelectedRecord] = useState<VolunteeringRecordDto>();
  const [selectedRecordConfirmed, setSelectedRecordConfirmed] = useState<boolean>();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const user = useSelector((state) => state.user);
  const currentUserRoles = useMemo(() => {
    const roles = [];
    if (user?.isAdministrator) {
      roles.push("Admin");
    }
    if (user?.isEducator) {
      roles.push("Educator");
    }
    return roles;
  }, [user]);

  const rows = useMemo(() => data?.map((data) => generateRow(data, userId ?? "")) ?? [], [data, userId]);

  const handleRowClick = useCallback(
    (params: GridRowParams<RowData>) => {
      console.log("handleRowClick params:", params);
      const record = data?.find((r) => r.id === params.row.recordId);
      console.log("handleRowClick found record:", record);
      setSelectedRecord(record);
      setSelectedRecordConfirmed(params.row.confirmed);
    },
    [data],
  );

  const handleClosePanel = useCallback(() => {
    setSelectedRecord(undefined);
    setSelectedRecordConfirmed(undefined);
  }, []);

  const setFilter = useCallback(
    (value: FilterFormValues[typeof FIELD_NAME_DATE_RANGE]) => {
      if (apiRef.current) {
        apiRef.current.setFilterModel({
          items: [{ field: "eventTime", operator: "between", value }],
        });
      }
    },
    [apiRef],
  );

  const clearFilter = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.setFilterModel({ items: [] });
      apiRef.current.setPage(0);
    }
  }, [apiRef]);

  const datePickerProps = useMemo(() => {
    return {
      disableFuture: true,
      minDate: rows.reduce((min: DateTime, row) => {
        // Find the earliest event time to set the min date
        if (!row.eventTime?.startTime) {
          return min;
        }

        const rowStartTime = row.eventTime.startTime;
        return rowStartTime < min ? rowStartTime : min;
      }, DateTime.now()),
    };
  }, [rows]);

  const visibleColumns = useMemo(() => {
    return columns.filter((column) => {
      // Hide Details and Time columns on smaller screens
      if (column.field === "rowId" || column.field === "totalMinutes") {
        return isLargeScreen;
      }
      return true;
    });
  }, [isLargeScreen]);

  return (
    <>
      <Box sx={{ overflow: "hidden", width: "100%" }}>
        <DataGrid<RowData>
          apiRef={apiRef}
          columns={visibleColumns}
          disableColumnMenu={false}
          onRowClick={handleRowClick}
          rows={rows}
          status={status}
          headerWidgets={
            isLargeScreen ? (
              <VolunteersHeaderWidgets
                datePickerProps={datePickerProps}
                setFilter={setFilter}
                clearFilter={clearFilter}
              />
            ) : null
          }
          slots={{
            toolbar: isLargeScreen ? undefined : null,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              display: "flex",
            },
            "& .MuiDataGrid-main": {
              width: "100% !important",
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "action.hover",
              },
              cursor: "pointer",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "hidden !important",
              width: "100% !important",
            },
            width: "100% !important",
          }}
        />
      </Box>
      <VolunteeringRecordPanel
        currentUserId={userId ?? ""}
        currentUserRoles={currentUserRoles}
        isOpen={!!selectedRecord}
        onClose={handleClosePanel}
        record={selectedRecord}
        confirmed={selectedRecordConfirmed}
      />
    </>
  );
};
